import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  leadColumnSchema,
  bodyColumnSchema,
  eepromColumnSchema,
  type LeadColumn,
  type BodyColumn,
  type EepromColumn,
} from '../../generics/CablePrimitives.js'
import {
  bomConfigVersionId,
  type BomConfigVersionId,
} from './BomConfigVersion.js'
import {
  cableConnectorType,
  type default as CableConnectorType,
} from './CableConnectorType.js'
import {
  cableHeatshrinkType,
  type default as CableHeatshrinkType,
} from './CableHeatshrinkType.js'
import { cableLockEventId, type CableLockEventId } from './CableLockEvent.js'
import { cablePriceId, type CablePriceId } from './CablePrice.js'
import {
  cableStatusType,
  type default as CableStatusType,
} from './CableStatusType.js'
import {
  lengthUnitType,
  type default as LengthUnitType,
} from './LengthUnitType.js'
import { orderId, type OrderId } from './Order.js'
import { orderLineItemId, type OrderLineItemId } from './OrderLineItem.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.cable_ */
export type CableId = number & { __brand: 'CableId' }

/** Represents the table public.cable_ */
export default interface CableTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<CableId, CableId | undefined, CableId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  name: ColumnType<string, string, string>

  /**
   * Database type: pg_catalog.int4
   * Manually assigned from cable_serial_seq when needed.
   */
  cableSerial: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.bool */
  locked: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.timestamptz */
  lockedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  lockedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.bool */
  mlink: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.text */
  internalNotes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  salesNotes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  productionNotes: ColumnType<string | null, string | null, string | null>

  /** Database type: public.cable_status_type_ */
  status: ColumnType<
    CableStatusType | null,
    CableStatusType | null,
    CableStatusType | null
  >

  /** Database type: pg_catalog.text */
  packaging: ColumnType<string | null, string | null, string | null>

  /** Database type: public.cable_heatshrink_type_ */
  heatshrink: ColumnType<
    CableHeatshrinkType | null,
    CableHeatshrinkType | null,
    CableHeatshrinkType | null
  >

  /** Database type: public.cable_connector_type_ */
  connector: ColumnType<
    CableConnectorType | null,
    CableConnectorType | null,
    CableConnectorType | null
  >

  /** Database type: pg_catalog.int4 */
  quantity: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.bool */
  panelBox: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.text */
  cableType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.jsonb */
  lead: ColumnType<LeadColumn | null, LeadColumn | null, LeadColumn | null>

  /** Database type: pg_catalog.jsonb */
  body: ColumnType<BodyColumn | null, BodyColumn | null, BodyColumn | null>

  /** Database type: pg_catalog.jsonb */
  eeprom: ColumnType<
    EepromColumn | null,
    EepromColumn | null,
    EepromColumn | null
  >

  /** Database type: pg_catalog.uuid */
  priceModelId: ColumnType<
    CablePriceId | null,
    CablePriceId | null,
    CablePriceId | null
  >

  /** Database type: pg_catalog.numeric */
  priceDiscountAmount: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.text */
  priceDiscountType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  priceDiscountRaw: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  priceDiscountReason: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.numeric */
  priceAdditionalChargesAmount: ColumnType<
    number | null,
    number | null,
    number | null
  >

  /** Database type: pg_catalog.text */
  priceAdditionalChargesNotes: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.numeric */
  unitPrice: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  totalPrice: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.numeric */
  totalWeight: ColumnType<number | null, number | null, number | null>

  /** Database type: public.length_unit_type_ */
  lengthUnits: ColumnType<
    LengthUnitType,
    LengthUnitType | undefined,
    LengthUnitType
  >

  /** Database type: pg_catalog.int4 */
  displaySort: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.uuid */
  orderId: ColumnType<OrderId | null, OrderId | null, OrderId | null>

  /** Database type: pg_catalog.uuid */
  orderLineItemId: ColumnType<
    OrderLineItemId | null,
    OrderLineItemId | null,
    OrderLineItemId | null
  >

  /** Database type: pg_catalog.text */
  erpnextQuotationId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  erpnextQuotationItemId: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.text */
  itemCode: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  erpnextSalesOrderId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  erpnextSalesOrderItemId: ColumnType<
    string | null,
    string | null,
    string | null
  >

  /** Database type: pg_catalog.bool */
  sdm: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.bool */
  idm: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.bool */
  rigidProbe: ColumnType<boolean | null, boolean | null, boolean | null>

  /** Database type: pg_catalog.text */
  description: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.int4 */
  parentCableId: ColumnType<CableId | null, CableId | null, CableId | null>

  /** Database type: pg_catalog.uuid */
  bomConfigVersionId: ColumnType<
    BomConfigVersionId | null,
    BomConfigVersionId | null,
    BomConfigVersionId | null
  >

  /** Database type: pg_catalog.uuid */
  cableLockEventId: ColumnType<
    CableLockEventId | null,
    CableLockEventId | null,
    CableLockEventId | null
  >
}

export type Cable = Selectable<CableTable>

export type CableInitializer = Insertable<CableTable>

export type CableMutator = Updateable<CableTable>

export const cableId = z.number() as unknown as z.Schema<CableId>

export const cable = z.object({
  id: cableId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  name: z.string(),
  cableSerial: z.number().nullable().optional(),
  locked: z.boolean().nullable().optional(),
  lockedAt: z.coerce.date().nullable().optional(),
  lockedBy: userId.nullable().optional(),
  mlink: z.boolean().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  salesNotes: z.string().nullable().optional(),
  productionNotes: z.string().nullable().optional(),
  status: cableStatusType.nullable().optional(),
  packaging: z.string().nullable().optional(),
  heatshrink: cableHeatshrinkType.nullable().optional(),
  connector: cableConnectorType.nullable().optional(),
  quantity: z.number().nullable().optional(),
  panelBox: z.boolean().nullable().optional(),
  cableType: z.string().nullable().optional(),
  lead: leadColumnSchema,
  body: bodyColumnSchema,
  eeprom: eepromColumnSchema,
  priceModelId: cablePriceId.nullable().optional(),
  priceDiscountAmount: z.number().nullable().optional(),
  priceDiscountType: z.string().nullable().optional(),
  priceDiscountRaw: z.string().nullable().optional(),
  priceDiscountReason: z.string().nullable().optional(),
  priceAdditionalChargesAmount: z.number().nullable().optional(),
  priceAdditionalChargesNotes: z.string().nullable().optional(),
  unitPrice: z.number().nullable().optional(),
  totalPrice: z.number().nullable().optional(),
  totalWeight: z.number().nullable().optional(),
  lengthUnits: lengthUnitType,
  displaySort: z.number().nullable().optional(),
  orderId: orderId.nullable().optional(),
  orderLineItemId: orderLineItemId.nullable().optional(),
  erpnextQuotationId: z.string().nullable().optional(),
  erpnextQuotationItemId: z.string().nullable().optional(),
  itemCode: z.string().nullable().optional(),
  erpnextSalesOrderId: z.string().nullable().optional(),
  erpnextSalesOrderItemId: z.string().nullable().optional(),
  sdm: z.boolean().nullable().optional(),
  idm: z.boolean().nullable().optional(),
  rigidProbe: z.boolean().nullable().optional(),
  description: z.string().nullable().optional(),
  parentCableId: cableId.nullable().optional(),
  bomConfigVersionId: bomConfigVersionId.nullable().optional(),
  cableLockEventId: cableLockEventId.nullable().optional(),
}) as unknown as z.Schema<Cable>

export const cableInitializer = z.object({
  id: cableId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  name: z.string(),
  cableSerial: z.number().optional().nullable(),
  locked: z.boolean().optional().nullable(),
  lockedAt: z.coerce.date().optional().nullable(),
  lockedBy: userId.optional().nullable(),
  mlink: z.boolean().optional().nullable(),
  internalNotes: z.string().optional().nullable(),
  salesNotes: z.string().optional().nullable(),
  productionNotes: z.string().optional().nullable(),
  status: cableStatusType.optional().nullable(),
  packaging: z.string().optional().nullable(),
  heatshrink: cableHeatshrinkType.optional().nullable(),
  connector: cableConnectorType.optional().nullable(),
  quantity: z.number().optional().nullable(),
  panelBox: z.boolean().optional().nullable(),
  cableType: z.string().optional().nullable(),
  lead: leadColumnSchema,
  body: bodyColumnSchema,
  eeprom: eepromColumnSchema,
  priceModelId: cablePriceId.optional().nullable(),
  priceDiscountAmount: z.number().optional().nullable(),
  priceDiscountType: z.string().optional().nullable(),
  priceDiscountRaw: z.string().optional().nullable(),
  priceDiscountReason: z.string().optional().nullable(),
  priceAdditionalChargesAmount: z.number().optional().nullable(),
  priceAdditionalChargesNotes: z.string().optional().nullable(),
  unitPrice: z.number().optional().nullable(),
  totalPrice: z.number().optional().nullable(),
  totalWeight: z.number().optional().nullable(),
  lengthUnits: lengthUnitType.optional(),
  displaySort: z.number().optional().nullable(),
  orderId: orderId.optional().nullable(),
  orderLineItemId: orderLineItemId.optional().nullable(),
  erpnextQuotationId: z.string().optional().nullable(),
  erpnextQuotationItemId: z.string().optional().nullable(),
  itemCode: z.string().optional().nullable(),
  erpnextSalesOrderId: z.string().optional().nullable(),
  erpnextSalesOrderItemId: z.string().optional().nullable(),
  sdm: z.boolean().optional().nullable(),
  idm: z.boolean().optional().nullable(),
  rigidProbe: z.boolean().optional().nullable(),
  description: z.string().optional().nullable(),
  parentCableId: cableId.optional().nullable(),
  bomConfigVersionId: bomConfigVersionId.optional().nullable(),
  cableLockEventId: cableLockEventId.optional().nullable(),
}) as unknown as z.Schema<CableInitializer>

export const cableMutator = z.object({
  id: cableId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  name: z.string().optional(),
  cableSerial: z.number().optional().nullable(),
  locked: z.boolean().optional().nullable(),
  lockedAt: z.coerce.date().optional().nullable(),
  lockedBy: userId.optional().nullable(),
  mlink: z.boolean().optional().nullable(),
  internalNotes: z.string().optional().nullable(),
  salesNotes: z.string().optional().nullable(),
  productionNotes: z.string().optional().nullable(),
  status: cableStatusType.optional().nullable(),
  packaging: z.string().optional().nullable(),
  heatshrink: cableHeatshrinkType.optional().nullable(),
  connector: cableConnectorType.optional().nullable(),
  quantity: z.number().optional().nullable(),
  panelBox: z.boolean().optional().nullable(),
  cableType: z.string().optional().nullable(),
  lead: leadColumnSchema,
  body: bodyColumnSchema,
  eeprom: eepromColumnSchema,
  priceModelId: cablePriceId.optional().nullable(),
  priceDiscountAmount: z.number().optional().nullable(),
  priceDiscountType: z.string().optional().nullable(),
  priceDiscountRaw: z.string().optional().nullable(),
  priceDiscountReason: z.string().optional().nullable(),
  priceAdditionalChargesAmount: z.number().optional().nullable(),
  priceAdditionalChargesNotes: z.string().optional().nullable(),
  unitPrice: z.number().optional().nullable(),
  totalPrice: z.number().optional().nullable(),
  totalWeight: z.number().optional().nullable(),
  lengthUnits: lengthUnitType.optional(),
  displaySort: z.number().optional().nullable(),
  orderId: orderId.optional().nullable(),
  orderLineItemId: orderLineItemId.optional().nullable(),
  erpnextQuotationId: z.string().optional().nullable(),
  erpnextQuotationItemId: z.string().optional().nullable(),
  itemCode: z.string().optional().nullable(),
  erpnextSalesOrderId: z.string().optional().nullable(),
  erpnextSalesOrderItemId: z.string().optional().nullable(),
  sdm: z.boolean().optional().nullable(),
  idm: z.boolean().optional().nullable(),
  rigidProbe: z.boolean().optional().nullable(),
  description: z.string().optional().nullable(),
  parentCableId: cableId.optional().nullable(),
  bomConfigVersionId: bomConfigVersionId.optional().nullable(),
  cableLockEventId: cableLockEventId.optional().nullable(),
}) as unknown as z.Schema<CableMutator>
